import { WebPubSubClient } from '@azure/web-pubsub-client';
import { Typography } from 'src/components/ui/typography';
import { enc } from 'crypto-js';
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ENCRPT_KEY_B64, ENV_BASE_URL } from 'src/config';
import {
  patchProcessDocumentsCache,
  patchSyncDocumentsCache
} from 'src/content/DataLibrary/utils';
import {
  subCategoryKeyMap,
  subCategorySummaryKeyMap
} from 'src/content/Notifications/constants';
import { getTranslatedText } from 'src/content/Notifications/utils';
import {
  WORKFLOW_RUN_EVENT_KINDS,
  workflowRunEventKind,
  type WorkflowExecutionPubSubPayload,
  type WorkflowExecutionQueuedEvent
} from 'src/content/Workflows/data/workflowRunEvents';
import useLazyQuery from 'src/hooks/useLazyQuery';
import { setAlert } from 'src/redux/slices/snackbar';
import { RootState, useDispatch, useSelector } from 'src/redux/store';
import { api, useLazyGetUploadedFilesStatusQuery } from 'src/services/api';
import { decryptResponse } from 'src/services/utils';
import { StringKeys } from 'src/types/base';
import {
  AnalysisQuestionQueuedEvent,
  AnalysisResponseProps
} from 'src/types/docAnalyst';
import {
  ACCOUNT_TYPE,
  ALERT,
  NOTIFICATION_EVENT_TYPE,
  PUBSUB_EVENT_TYPE,
  SMART_MEET_EVENT_TYPE
} from 'src/types/enum';
import logger from 'src/utils/logger';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { getUploadedFileIds, removeUploadingFilesId } from 'src/utils/utils';

type AnalysisDataProps = {
  id: number;
  status: string;
};

type ActionsConnectorDataProps = {
  actions_ids: number[];
  connector_id: number;
  action_category_id: number;
};

type AzureWebPubSubUserContextType = {
  connectionId: string;
  processedData: StringKeys[];
  syncedData: StringKeys;
  actionsConnectorData: ActionsConnectorDataProps;
  analysisQuestionEvents?: AnalysisQuestionQueuedEvent[];
  dropAnalysisQuestionEventsUpTo?: (seq: number) => void;
  registerAnalysisQuestionConsumer?: () => () => void;
  runningAnalysisStatuses: Record<string, AnalysisDataProps>;
  workflowExecutionEvents?: WorkflowExecutionQueuedEvent[];
  /** Called by the consumer with the highest `seq` it has handled. */
  dropWorkflowExecutionEventsUpTo?: (seq: number) => void;
  /**
   * Must be called on mount by anything that drains `workflowExecutionEvents`;
   * returns its unregister. Nothing is queued while no consumer is registered.
   */
  registerWorkflowExecutionConsumer?: () => () => void;
  resetContext: () => void;
  clearActionsData: () => void;
};

export const AzureWebPubSubUserContext =
  createContext<AzureWebPubSubUserContextType>(
    {} as AzureWebPubSubUserContextType
  );

export const AzureWebPubSubUserProvider: FC = ({ children }) => {
  const { t } = useTranslation();
  const [pubsubClient, setPubsubClient] = useState<any>(null);
  const [connectionId, setConnectionId] = useState<string>();
  const { azureWebPubSubInfo, user } = useSelector((state) => state.auth);
  const [actionsConnectorData, setActionsConnectorData] =
    useState<ActionsConnectorDataProps>(undefined);

  const [processedData, setProcessedData] = useState<StringKeys[]>([]);
  const [syncedData, setSyncedData] = useState<StringKeys>({});
  const { tenant_info } = useSelector((state: RootState) => state.library);
  const tenantId = tenant_info?.id;
  const tenantNamespaceId = tenant_info?.namespace_id;
  const [fetchUploadedFilesStatus] = useLazyQuery({
    api: useLazyGetUploadedFilesStatusQuery
  });
  const [analysisQuestionEvents, setAnalysisQuestionEvents] = useState<
    AnalysisQuestionQueuedEvent[]
  >([]);
  const analysisQuestionSeqRef = useRef(0);
  const analysisQuestionConsumerCountRef = useRef(0);
  const [runningAnalysisStatuses, setRunningAnalysisStatuses] = useState<
    Record<string, AnalysisDataProps>
  >({});
  const [workflowExecutionEvents, setWorkflowExecutionEvents] = useState<
    WorkflowExecutionQueuedEvent[]
  >([]);
  const workflowEventSeqRef = useRef(0);
  const workflowConsumerCountRef = useRef(0);
  const isMounted = useRef(true);
  const location = useLocation();
  const dispatch = useDispatch();

  /**
   * Registers a queue consumer. This provider outlives the workflow builder, and
   * scheduled / webhook-triggered runs keep emitting while the builder is closed —
   * with no consumer mounted nothing would ever drain the queue, so it must not be
   * filled in the first place.
   */
  const registerWorkflowExecutionConsumer = useCallback(() => {
    workflowConsumerCountRef.current += 1;
    return () => {
      workflowConsumerCountRef.current -= 1;
    };
  }, []);

  const enqueueWorkflowExecutionEvent = (
    payload: WorkflowExecutionPubSubPayload
  ) => {
    if (workflowConsumerCountRef.current === 0) {
      return;
    }
    // Seq is bumped outside the updater so a double-invoked updater (StrictMode)
    // can't hand two events the same number.
    workflowEventSeqRef.current += 1;
    const seq = workflowEventSeqRef.current;
    setWorkflowExecutionEvents((prev) => [...prev, { seq, payload }]);
  };

  const registerAnalysisQuestionConsumer = useCallback(() => {
    analysisQuestionConsumerCountRef.current += 1;
    return () => {
      analysisQuestionConsumerCountRef.current -= 1;
    };
  }, []);

  const enqueueAnalysisQuestionEvent = (payload: AnalysisResponseProps) => {
    if (analysisQuestionConsumerCountRef.current === 0) {
      return;
    }

    analysisQuestionSeqRef.current += 1;
    const seq = analysisQuestionSeqRef.current;
    setAnalysisQuestionEvents((prev) => [...prev, { seq, payload }]);
  };

  const handleNotificationAlert = (data: any) => {
    setTimeout(
      () => dispatch(api.util.invalidateTags(['Notifications'])),
      1000
    );
    dispatch(
      setAlert({
        msg: (
          <div>
            <Typography variant="h6" align="left">
              {data?.sub_category_name
                ? t(
                    subCategoryKeyMap[data.sub_category_name] ??
                      data.sub_category_name
                  )
                : ''}
            </Typography>
            <Typography variant="b3" align="left">
              {getTranslatedText(
                subCategorySummaryKeyMap,
                data?.sub_category_name,
                data?.summary_vars,
                t
              )}
            </Typography>
          </div>
        ),
        type: ALERT.INFO
      })
    );
  };

  useEffect(() => {
    const wpsClient = new WebPubSubClient(azureWebPubSubInfo.url);
    wpsClient.on('connected', (e) => {
      logger.log('connected user wps connection', e.connectionId);
      setConnectionId(e.connectionId);
      setPubsubClient(wpsClient);
    });
    wpsClient.on('disconnected', (e) => {
      logger.log('disconnected user wps connection', e.connectionId);
      setConnectionId(undefined);
      setPubsubClient(null);
    });
    wpsClient.on('server-message', ({ message }: any) => {
      const key_db64 = enc.Utf8.stringify(
        enc.Base64.parse(ENCRPT_KEY_B64 || '')
      );

      const key_bdb64 = enc.Utf8.parse(key_db64.slice(0, 32));
      const data = decryptResponse(message, key_bdb64);

      if (data?.event_type === PUBSUB_EVENT_TYPE.REFRESH_TOKEN) {
        if (data?.data?.user_tenant_id === tenantId) {
          dispatch(
            setAlert({
              msg: t(T.accountTypeUpdateMsg),
              type: ALERT.WARNING
            })
          );
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      } else if (data?.event_type === PUBSUB_EVENT_TYPE.USER_DELETED) {
        dispatch(
          setAlert({
            msg: t(T.userDeletedMsg),
            type: ALERT.WARNING
          })
        );
        setTimeout(() => {
          window.location.href = ENV_BASE_URL;
        }, 3000);
      } else if (
        data.event_type === PUBSUB_EVENT_TYPE.DOC_ANALYSIS_QUESTIONS_EVENT
      ) {
        enqueueAnalysisQuestionEvent(data?.data);
      } else if (data.event_type === PUBSUB_EVENT_TYPE.DOC_ANALYSIS_EVENT) {
        const analysisId = data?.data?.id as AnalysisDataProps['id'];
        if (analysisId !== undefined && analysisId !== null) {
          setRunningAnalysisStatuses((prev) => ({
            ...prev,
            [analysisId]: {
              id: analysisId,
              status: data?.data?.status_code
            }
          }));
        }
      } else if (data.event_type === PUBSUB_EVENT_TYPE.ACTION_CONNECTOR_EVENT) {
        setActionsConnectorData(data?.data);
      } else if (data?.event_type === NOTIFICATION_EVENT_TYPE.NOTIFICATION) {
        handleNotificationAlert(data);
      } else if (
        data?.event_type === SMART_MEET_EVENT_TYPE.RESPONSE ||
        data?.event_type === SMART_MEET_EVENT_TYPE.STATUS
      ) {
        // Deliberately an array carrying the event's fields — SmartMeet reads
        // this slot as an object while every other consumer iterates it.
        setProcessedData(Object.assign([], data));
      } else if (WORKFLOW_RUN_EVENT_KINDS.has(workflowRunEventKind(data))) {
        enqueueWorkflowExecutionEvent(data as WorkflowExecutionPubSubPayload);
      }
    });

    wpsClient.on('group-message', ({ message }: any) => {
      onGroupMessage(message);
    });

    wpsClient.start();
    return () => {
      try {
        if (tenantNamespaceId) {
          wpsClient?.leaveGroup(`data-library-${tenantNamespaceId}`);
        }
        if (tenantId) {
          wpsClient?.leaveGroup(`realm-${tenantId}`);
          wpsClient?.leaveGroup(`org-admins-${tenantId}`);
          wpsClient?.leaveGroup(`org-members-${tenantId}`);
        }
      } catch (error) {
        logger.error('Error in leaving group', error);
      }
      isMounted.current = false;
      wpsClient?.stop();
    };
  }, [azureWebPubSubInfo.url]);

  useEffect(() => {
    if (connectionId && pubsubClient) {
      if (tenantNamespaceId) {
        pubsubClient?.joinGroup(`data-library-${tenantNamespaceId}`);
      }
      if (tenantId) {
        pubsubClient?.joinGroup(`realm-${tenantId}`);
      }

      if (tenantId) {
        pubsubClient?.joinGroup(`org-members-${tenantId}`);
        const isOrgAdmin =
          user?.accountType === ACCOUNT_TYPE.ORGANIZATION_ADMINISTRATOR ||
          user?.accountType === ACCOUNT_TYPE.ORGANIZATION_OWNER;
        if (isOrgAdmin) {
          pubsubClient?.joinGroup(`org-admins-${tenantId}`);
        }
      }

      const uploadingFileIds = getUploadedFileIds()
        ? JSON.parse(getUploadedFileIds())
        : [];

      if (uploadingFileIds.length) {
        fetchUploadedFilesStatus({
          params: {
            params: { file_ids: uploadingFileIds }
          },
          successCallback(data) {
            if (data.length > 0) {
              setProcessedData(data);
              removeUploadingFilesId();
            }
          }
        });
      }
    }
  }, [connectionId, pubsubClient]);

  const resetContext = () => {
    if (isMounted.current) {
      setProcessedData([]);
      setSyncedData({});
      setWorkflowExecutionEvents([]);
      setAnalysisQuestionEvents([]);
      setRunningAnalysisStatuses({});
    }
  };

  const dropWorkflowExecutionEventsUpTo = useCallback((seq: number) => {
    setWorkflowExecutionEvents((prev) => {
      const next = prev.filter((e) => e.seq > seq);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const dropAnalysisQuestionEventsUpTo = useCallback((seq: number) => {
    setAnalysisQuestionEvents((prev) => {
      const next = prev.filter((e) => e.seq > seq);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const clearActionsData = () => {
    setActionsConnectorData(undefined);
  };

  const onGroupMessage = (message) => {
    const key_db64 = enc.Utf8.stringify(enc.Base64.parse(ENCRPT_KEY_B64 || ''));
    const key_bdb64 = enc.Utf8.parse(key_db64.slice(0, 32));
    const data = decryptResponse(message, key_bdb64);
    if (data?.event_type === NOTIFICATION_EVENT_TYPE.NOTIFICATION) {
      if (data?.target_users?.includes(user?.username)) {
        handleNotificationAlert(data);
      }
    } else if (
      data?.event_type === NOTIFICATION_EVENT_TYPE.ORG_NOTIFICATION_PREF_UPDATED
    ) {
      setTimeout(
        () =>
          dispatch(
            api.util.invalidateTags([
              'NotificationPreferences',
              'OrganizationNotificationPreferences'
            ])
          ),
        100
      );
    } else if (
      data?.event_type == PUBSUB_EVENT_TYPE.FILE_PROCESSING_CSC_EVENT
    ) {
      setProcessedData(data?.state_change);
      dispatch(patchProcessDocumentsCache(data?.state_change));
    } else if (data?.event_type == PUBSUB_EVENT_TYPE.FILE_SYNC_CSC_EVENT) {
      setSyncedData(data?.state_change);
      dispatch(patchSyncDocumentsCache(data?.state_change));
    } else if (data?.event_type === PUBSUB_EVENT_TYPE.REALM_DELETED) {
      if (data?.tenant_id === tenantId) {
        dispatch(
          setAlert({
            msg: t(T.realmDeletedMsg),
            type: ALERT.WARNING
          })
        );
        setTimeout(() => {
          window.location.href = ENV_BASE_URL;
        }, 3000);
      }
    }
  };

  return (
    <AzureWebPubSubUserContext.Provider
      value={{
        connectionId,
        processedData,
        resetContext,
        syncedData,
        analysisQuestionEvents,
        dropAnalysisQuestionEventsUpTo,
        registerAnalysisQuestionConsumer,
        runningAnalysisStatuses,
        actionsConnectorData,
        clearActionsData,
        workflowExecutionEvents,
        dropWorkflowExecutionEventsUpTo,
        registerWorkflowExecutionConsumer
      }}
    >
      {children}
    </AzureWebPubSubUserContext.Provider>
  );
};
